import { Box, InputAdornment, MenuItem, Select, TextField, TextFieldProps } from '@mui/material';
import strings from 'common/Translation/Translate';
import { FormValidators } from 'components/Form';
import { CountryCode, getCountryCallingCode, parsePhoneNumber, parsePhoneNumberWithError } from 'libphonenumber-js';
import { FC, useState } from 'react';
import { FlagList } from 'utils/validators/HelperComponents';
import { getCountryNameByCountryCode } from 'utils/validators/HelperFunctions';
import { validateInput } from 'utils/validators/Validators';

interface OwnProps {
  leftIcon?: string;
  inputValue?: any;
  field: string;
  typeValue?: string;
  textChange?: (value: any, field: string, error?: { error: boolean; message: string }) => void;
  validators?: FormValidators[];
  hasError?: boolean;
  fieldError?: boolean | string;
  multiline?: boolean;
  rows?: number | string;
  minRows?: number | string;
  maxRows?: number | string;
  infoText?: any;
  required: boolean;
  defaultCountry: CountryCode;
}
const PhoneInputField: FC<OwnProps & TextFieldProps> = ({
  hasError,
  validators,
  textChange,
  field,
  inputValue,
  fieldError,
  inputProps,
  variant,
  disabled,
  label,
  size = 'small',
  style,
  autoFocus,
  sx,
  className,
  defaultCountry,
  required,
  ...rest
}) => {
  const [error, setError] = useState<any>(null);

  const handleChange = (value: string, countryCode: any) => {
    let newValue = '';
    let inputError = { error: false, message: '' };
    if (value === '' && !required) {
      setError(null);
      if (textChange) {
        textChange({ countryCode: countryCode, value: newValue }, field, inputError);
      }
      return;
    }
    try {
      if (countryCode !== inputValue.countryCode) {
        newValue = '+' + getCountryCallingCode(countryCode).toString();
        newValue += value != '' ? parsePhoneNumberWithError(value).nationalNumber : '';
        if (!Number.isNaN(Number(newValue))) {
          inputError = validateInput(validators, newValue);
          setError(inputError);
        }
      } else {
        newValue = value;
        const possibleCountries = parsePhoneNumberWithError(value).getPossibleCountries();
        if (!possibleCountries.includes(countryCode)) {
          countryCode = possibleCountries[0];
        }
        console.log(possibleCountries);
      }
      if (!parsePhoneNumber(newValue, countryCode).isValid()) {
        inputError = { error: true, message: strings.phone_no_invalid_error };
        setError(inputError);
      }
    } catch (e) {
      inputError = { error: true, message: strings.phone_no_invalid_error };
      setError(inputError);
    }
    if (!inputError.error) {
      setError(null);
    }
    if (textChange) {
      textChange({ countryCode: countryCode, value: newValue }, field, inputError);
    }
  };
  const customMessage = () => {
    if (!error && hasError && fieldError) {
      return strings.requiredText;
    } else return '';
  };

  return (
    <TextField
      value={inputValue.value || `+${getCountryCallingCode(inputValue.countryCode || defaultCountry)}`}
      onChange={e => handleChange(e.target.value, inputValue.countryCode)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <Select
              sx={{
                minWidth: '60px',
                '.MuiSelect-select > div > span': {
                  display: 'none',
                },
                border: 0,
                boxShadow: 'none',
                '&.Mui-focused ': {
                  border: 0,
                  background: 'white !important',
                },
              }}
              variant="standard"
              disableUnderline
              value={inputValue.countryCode || defaultCountry}
              onChange={e => handleChange(inputValue.value, e.target.value)}>
              {FlagList.map((item, i) => (
                <MenuItem key={item.value} value={item.value} sx={{ minWidth: '300px' }}>
                  <>{item.label}</>
                  <Box display="flex" alignItems="center" justifyContent="space-between" ml={1} width={'100%'}>
                    <span>{getCountryNameByCountryCode(item.value)}</span>
                    <span>+{getCountryCallingCode(item.value).toString()}</span>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </InputAdornment>
        ),
      }}
      autoFocus={autoFocus}
      sx={{
        ...sx,
      }}
      size={size}
      autoComplete={(inputProps as { autoComplete?: string } | undefined)?.autoComplete ?? 'off'}
      inputProps={inputProps}
      disabled={disabled}
      helperText={error && error.error ? error.message : customMessage()}
      error={(error && error.error) || (!error && hasError && fieldError) ? true : false}
      color="primary"
      fullWidth
      className={`input-field ${fieldError}`}
      label={label}
      variant={variant}
    />
  );
};

export default PhoneInputField;
